import * as React from 'react';
import { useState } from 'react';

/** Material UI Packages. */
import { Typography, Box, Select, MenuItem, IconButton } from '@mui/material';

/** Material UI Icons. */
import CloseIcon from '@mui/icons-material/Close';

/** Value holds the ID, but the label displays the CONTENT value. */
export function SelectField({ label, options, value, sx, onChange, name, labelStyle, selectStyle }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '25px',
                ...sx,
            }}
        >
            <Typography
                sx={{
                    display: 'inline',
                    color: '#003a52',
                    fontSize: '13px',
                    fontWeight: 500,
                    width: '100px',
                    ...labelStyle,
                }}
            >
                {label}
            </Typography>
            <Select
                name={name}
                value={value}
                onChange={onChange}
                sx={{
                    fontSize: '13px',
                    '& .MuiSelect-select': {
                        p: 1,
                    },
                    height: '27px',
                    overflow: 'hidden',
                    ...selectStyle,
                }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            maxHeight: 200,
                        },
                    },
                }}
            >
                {options.map((item) => {
                    return (
                        <MenuItem
                            value={item.id}
                            key={item.id}
                            name={name}
                            sx={{
                                fontSize: '13px',
                            }}
                        >
                            {item.content}
                        </MenuItem>
                    );
                })}
            </Select>
        </Box>
    );
}

/** Value holds the ID, but the label displays the CONTENT value. 'None' option is included. */
export function SelectField_None({ label, options, value, sx, onChange, name, labelStyle, selectStyle }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '25px',
                ...sx,
            }}
        >
            <Typography
                sx={{
                    display: 'inline',
                    color: '#003a52',
                    fontSize: '13px',
                    fontWeight: 500,
                    width: '100px',
                    ...labelStyle,
                }}
            >
                {label}
            </Typography>
            <Select
                name={name}
                value={value}
                onChange={onChange}
                sx={{
                    fontSize: '13px',
                    '& .MuiSelect-select': {
                        p: 1,
                    },
                    height: '27px',
                    overflow: 'hidden',
                    ...selectStyle,
                }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            maxHeight: 200,
                        },
                    },
                }}
            >
                <MenuItem value="" sx={{ fontSize: '13px' }}>
                    <em>None</em>
                </MenuItem>
                {options.map((item) => {
                    return (
                        <MenuItem
                            key={item.id}
                            value={item.id}
                            sx={{
                                fontSize: '13px',
                            }}
                        >
                            {item.content}
                        </MenuItem>
                    );
                })}
            </Select>
        </Box>
    );
}

/** Value & label apply the CONTENT value. 'None' option is included. */
export function SelectField_single({
    label,
    options,
    value,
    sx,
    onChange,
    name,
    selectStyle,
    labelStyle,
    disabled,
    error,
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '25px',
                ...sx,
            }}
        >
            <Typography
                sx={{
                    display: 'inline',
                    color: '#003a52',
                    fontSize: '13px',
                    fontWeight: 500,
                    width: '100px',
                    ...labelStyle,
                }}
            >
                {label}
            </Typography>
            <Select
                name={name}
                value={value}
                onChange={onChange}
                error={error}
                disabled={disabled}
                sx={{
                    fontSize: '13px',
                    '& .MuiSelect-select': {
                        p: 1,
                    },
                    height: '27px',
                    overflow: 'hidden',
                    ...selectStyle,
                }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            maxHeight: 200,
                        },
                    },
                }}
            >
                <MenuItem value="" sx={{ fontSize: '13px' }}>
                    <em>None</em>
                </MenuItem>
                {options.map((item, index) => {
                    return (
                        <MenuItem
                            value={item}
                            key={index}
                            name={item}
                            disabled={disabled}
                            sx={{
                                fontSize: '13px',
                            }}
                        >
                            {item}
                        </MenuItem>
                    );
                })}
            </Select>
        </Box>
    );
}

/** Value & label apply the CONTENT value.*/
export function SelectField_single_None({
    label,
    options,
    value,
    sx,
    onChange,
    name,
    selectStyle,
    labelStyle,
    disabled,
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '25px',
                ...sx,
            }}
        >
            <Typography
                sx={{
                    display: 'inline',
                    color: '#003a52',
                    fontSize: '13px',
                    fontWeight: 500,
                    width: '100px',
                    ...labelStyle,
                }}
            >
                {label}
            </Typography>
            <Select
                name={name}
                value={value || ''}
                onChange={onChange}
                sx={{
                    fontSize: '13px',
                    '& .MuiSelect-select': {
                        p: 1,
                    },
                    height: '27px',
                    overflow: 'hidden',
                    ...selectStyle,
                }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            maxHeight: 200,
                        },
                    },
                }}
            >
                {options.map((item, index) => {
                    return (
                        <MenuItem
                            value={item}
                            key={index}
                            name={item}
                            disabled={disabled}
                            sx={{
                                fontSize: '13px',
                            }}
                        >
                            {item}
                        </MenuItem>
                    );
                })}
            </Select>
        </Box>
    );
}

/** Value holds the ID, but the label displays the CONTENT value. Mulitiple choices are available. */
export function MultiSelectField({ label, options, value, sx, onChange, name, selectStyle, labelStyle }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '25px',
                ...sx,
            }}
        >
            <Typography
                sx={{
                    display: 'inline',
                    color: '#003a52',
                    fontSize: '13px',
                    fontWeight: 500,
                    width: '100px',
                    ...labelStyle,
                }}
            >
                {label}
            </Typography>
            <Select
                name={name}
                value={value}
                onChange={onChange}
                multiple
                renderValue={(selected) => {
                    const selectedContent = selected
                        .map((id) => {
                            const option = options.find((item) => item.id === id);
                            return option ? option.content : '';
                        })
                        .filter((content) => content !== '');
                    return selectedContent.join(', ');
                }}
                sx={{
                    fontSize: '13px',
                    '& .MuiSelect-select': { p: 1 },
                    height: '27px',
                    overflow: 'hidden',
                    ...selectStyle,
                }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            maxHeight: 200,
                        },
                    },
                }}
            >
                {options.map((item) => {
                    return (
                        <MenuItem
                            value={item.id}
                            key={item.id}
                            name={item}
                            sx={{
                                fontSize: '13px',
                            }}
                        >
                            {item.content}
                        </MenuItem>
                    );
                })}
            </Select>
        </Box>
    );
}

export function SelectField_delete({ value, onChange, options, disableDelete, handleDelete, selectStyle }) {
    return (
        <Select
            value={value}
            onChange={onChange}
            sx={{
                fontSize: '13px',
                '& .MuiSelect-select': {
                    p: 1,
                },
                width: '15vw',
                height: '25px',
                overflow: 'hidden',
                pl: 0.6,
                ...selectStyle,
            }}
            renderValue={(selected) => {
                if (Object.keys(selected).length === 0) {
                    return <Typography sx={{ fontSize: '13px', color: 'gray' }}>Press Create Button</Typography>;
                }
                return (
                    <Typography
                        title={selected.name}
                        sx={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                        {selected.name}
                    </Typography>
                );
            }}
            MenuProps={{
                PaperProps: {
                    sx: {
                        maxHeight: 200,
                    },
                },
            }}
        >
            {options?.length > 0 &&
                options.map((state, index) => {
                    return (
                        <MenuItem
                            key={index}
                            value={state}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 1,
                                width: '15vw',
                                minHeight: '30px',
                                py: 0.3,
                            }}
                        >
                            <Typography
                                sx={{
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    fontSize: '13px',
                                }}
                            >
                                {state.name}
                            </Typography>
                            {disableDelete && (
                                <IconButton size="small" onClick={(event) => handleDelete(event, state.id)}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            )}
                        </MenuItem>
                    );
                })}
        </Select>
    );
}
